import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChartBarIcon, CurrencyDollarIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { isTablet, isMobile } from 'react-device-detect';
import cryptoDesktop from '../assets/sliders/crypto-desktop.png';
import cryptoMobile from '../assets/sliders/crypto-mobile.png';
import stocksDesktop from '../assets/sliders/stocks-desktop.png';
import stocksMobile from '../assets/sliders/stocks-mobile.png';
import retirementDesktop from '../assets/sliders/retirement-desktop.png';
import retirementMobile from '../assets/sliders/retirement-mobile.png';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "Invest in Digital Assets with Confidence",
      subtitle: "Unlock the future of finance with our secure crypto trading platform. Access Bitcoin, Ethereum, and 100+ cryptocurrencies with institutional-grade security.",
      desktopImage: cryptoDesktop,
      mobileImage: cryptoMobile,
      buttonText: "Start Trading",
      buttonLink: "/buy-crypto",
      icon: CurrencyDollarIcon,
      accent: "from-amber-500 to-orange-600",
      badge: "Cryptocurrency Trading"
    },
    {
      id: 2,
      title: "Build Your Stock Portfolio Today",
      subtitle: "Trade global equities with zero commissions. Get real-time market data, advanced charting tools, and expert research to make informed decisions.",
      desktopImage: stocksDesktop,
      mobileImage: stocksMobile,
      buttonText: "Explore Stocks",
      buttonLink: "/exchange",
      icon: ChartBarIcon,
      accent: "from-amber-500 to-orange-600",
      badge: "Stock Market Investing"
    },
    {
      id: 3,
      title: "Secure Your Retirement Future",
      subtitle: "Plan for tomorrow with our comprehensive retirement solutions. Tax-advantaged accounts, automated investing, and personalized advice.",
      desktopImage: retirementDesktop,
      mobileImage: retirementMobile,
      buttonText: "Plan Retirement",
      buttonLink: "/services/retirement-planning",
      icon: ShieldCheckIcon,
      accent: "from-amber-500 to-orange-600",
      badge: "Retirement Planning"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];
  const IconComponent = currentSlideData.icon;

  return (
    <section className="relative bg-gradient-to-br from-gray-50 to-white pt-20 lg:pt-24 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-amber-100 to-emerald-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-80px)]">
          {/* Left Content */}
      <AnimatePresence mode="wait">
        <motion.div 
              key={currentSlide}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="space-y-8"
            >
              {/* Icon Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className={`inline-flex items-center space-x-3 px-4 py-2 rounded-full bg-gradient-to-r ${currentSlideData.accent} bg-opacity-10 border border-gray-200`}
              >
                <IconComponent className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-semibold text-gray-700">{currentSlideData.badge}</span>
              </motion.div>

              {/* Title */}
              <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight"
              >
                {currentSlideData.title}
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-gray-600 leading-relaxed"
              >
                {currentSlideData.subtitle}
              </motion.p>

              {/* CTA Buttons */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to={currentSlideData.buttonLink}
                  className={`inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-gradient-to-r ${currentSlideData.accent} text-white font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300`}
                >
                  {currentSlideData.buttonText}
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-2xl border-2 border-gray-300 text-gray-700 font-semibold text-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-300"
                >
                  Learn More
                </Link>
                  </motion.div>

        {/* Slide Indicators */}
              <div className="flex space-x-3 pt-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentSlide ? 'w-12 bg-amber-600' : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        </motion.div>
      </AnimatePresence>

          {/* Right Image */}
      <AnimatePresence mode="wait">
        <motion.div 
              key={`image-${currentSlide}`}
              initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={isMobile ? currentSlideData.mobileImage : currentSlideData.desktopImage}
                  alt={currentSlideData.title}
                  className="w-full h-auto object-cover"
                />
                <div className={`absolute inset-0 bg-gradient-to-tr ${currentSlideData.accent} opacity-10`}></div>
                  </div>

              {/* Floating Stats */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 border border-gray-100"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentSlideData.accent} flex items-center justify-center`}>
                    <IconComponent className="w-6 h-6 text-white" />
                      </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">$5B+</p>
                    <p className="text-sm text-gray-600">Assets Under Management</p>
                  </div>
                </div>
              </motion.div>
                  </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white" fillOpacity="0.1"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
